import { useState, useEffect, useCallback } from "react";
import { isNative } from "@/lib/platform";

const checkStandalone = () => {
    if (typeof window === 'undefined') return false;
    return isNative() || 
           window.matchMedia('(display-mode: standalone)').matches || 
           window.navigator.standalone === true;
};

const checkIsIOS = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const safari = /^((?!chrome|android).)*safari/i.test(ua);
    return ios && safari;
};

export const usePWAInstall = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isIOS] = useState(() => checkIsIOS());
    const [isInstallable, setIsInstallable] = useState(() => checkIsIOS());
    const [isStandalone] = useState(() => checkStandalone());

    useEffect(() => {
        if (isStandalone) return;

        // Handle Android/Chrome/Edge standard install prompt
        const handleBeforeInstallPrompt = (e) => {
            // Prevent the mini-infobar from appearing on mobile
            e.preventDefault();
            // Stash the event so it can be triggered later.
            setDeferredPrompt(e);
            setIsInstallable(true);
        };

        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        };
    }, [isStandalone]);

    const promptInstall = useCallback(async () => {
        if (isIOS) {
            // iOS requires manual installation, so we return a specific status 
            // that the UI can use to show a tooltip/modal with instructions.
            return { outcome: 'ios_instructions_needed' };
        }

        if (!deferredPrompt) {
            console.warn("PWA prompt is not available.");
            return { outcome: 'unavailable' };
        }

        // Show the install prompt
        deferredPrompt.prompt();

        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice;
        
        // We've used the prompt, and can't use it again, throw it away
        setDeferredPrompt(null);
        setIsInstallable(false);

        return { outcome };
    }, [deferredPrompt, isIOS]);

    return {
        isInstallable,
        isIOS,
        isStandalone,
        promptInstall
    };
};
